import type { MouseEvent, ReactNode } from "react";

/** A menu item is a link, so it comes before a toggle or a button that sits above the menu. */
const FIRST_CHOICE = "a[href]";
const ANY_FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [role="menuitem"], [tabindex]:not([tabindex="-1"])';

/**
 * Where a skip link lands focus (#1253). Following a `#fragment` only scrolls and moves the
 * browser's starting point for the next Tab: it leaves focus on the body when the target is a
 * plain region. A target that already takes focus (`<main tabindex="-1">`) gets it. Otherwise
 * focus goes to the first link inside, then the first other control, and a region with nothing
 * to use takes focus itself.
 */
function focusTargetFor(target: HTMLElement): HTMLElement {
  if (target.hasAttribute("tabindex")) return target;
  return (
    target.querySelector<HTMLElement>(FIRST_CHOICE) ??
    target.querySelector<HTMLElement>(ANY_FOCUSABLE) ??
    target
  );
}

/**
 * Keyboard-first escape from the chrome (GUIDELINES.md §3.8).
 *
 * Off-screen until focused, then pinned below the 48px top bar. Rendered as the
 * first focusable thing on the page so the first Tab of a session offers it.
 */
export function SkipLink({ href, children }: { href: string; children: ReactNode }) {
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!href.startsWith("#")) return;
    const target = document.getElementById(href.slice(1));
    if (!target) return;
    const landing = focusTargetFor(target);
    if (landing === target && !target.hasAttribute("tabindex")) {
      target.setAttribute("tabindex", "-1");
    }
    event.preventDefault();
    landing.focus();
    landing.scrollIntoView?.({ block: "nearest" });
  };
  return (
    <a href={href} className="skip-link" onClick={onClick}>
      {children}
    </a>
  );
}
